import type { FunctionComponent } from 'react';
import React, { useLayoutEffect, useRef } from 'react';
import type { TextareaAutosizeProps } from 'react-textarea-autosize';
import Textarea from 'react-textarea-autosize';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import cx from 'classnames';

import styles from './Reply.less';

type RenderCallback = (height?: string | number) => void;

interface AnimateHeightProps {
  render: (
    setInitialHeight: RenderCallback,
    setTargetHeight: RenderCallback,
    endAnimation: RenderCallback,
  ) => React.ReactNode;
}

interface AnimateHeightState {
  height: string | number | undefined;
  isAnimating: boolean;
}

class AnimateHeight extends React.Component<
  AnimateHeightProps,
  AnimateHeightState
> {
  state = { height: 'auto', isAnimating: false };

  setInitialHeight = (height: string | number | undefined) => {
    this.setState({ height, isAnimating: true });
  };

  setTargetHeight = (height: string | number | undefined) => {
    const onAfterReflow = window.requestAnimationFrame || window.setTimeout;

    onAfterReflow(() => {
      this.setState({ height });
    });
  };

  endAnimation = () => {
    this.setState({ isAnimating: false });
  };

  render() {
    const { isAnimating, height } = this.state;

    const style = {
      height: isAnimating ? height : 'auto',
      transition: '200ms height',
    };

    return (
      <div style={style}>
        {this.props.render(
          this.setInitialHeight,
          this.setTargetHeight,
          this.endAnimation,
        )}
      </div>
    );
  }
}

export const ReplyContainer: FunctionComponent = ({ children, ...props }) => (
  <div className={styles.replyContainer} {...props}>
    <AnimateHeight
      // eslint-disable-next-line react/jsx-no-bind
      render={(setInitialHeight, setTargetHeight, endAnimation) => (
        <TransitionGroup>
          {React.Children.map(children, (child, index) => (
            <CSSTransition
              key={(child as React.ReactElement).key || index}
              timeout={400}
              classNames="slide"
              unmountOnExit={true}
              // eslint-disable-next-line react/jsx-no-bind
              onExit={(node) => setInitialHeight(node.offsetHeight)}
              // eslint-disable-next-line react/jsx-no-bind
              onEnter={(node) => setTargetHeight(node.offsetHeight)}
              // eslint-disable-next-line react/jsx-no-bind
              onExited={() => endAnimation()}
            >
              <div className={styles.slideIn}>{child}</div>
            </CSSTransition>
          ))}
        </TransitionGroup>
      )}
    />
  </div>
);

export const ReplyActions: FunctionComponent<{ centered?: boolean }> = ({
  centered,
  ...props
}) => (
  <div
    className={cx(styles.replyActions, centered && styles.centered)}
    {...props}
  />
);

export const ReplyColumns: FunctionComponent = (props) => (
  <div className={styles.replyColumns} {...props} />
);

export const ReplyColumnAvatar: FunctionComponent = (props) => (
  <div className={styles.replyColumnAvatar} {...props} />
);

export const ReplyColumnContent: FunctionComponent = (props) => (
  <div className={styles.replyColumnContent} {...props} />
);

export const ReplyLink: FunctionComponent<
  React.AnchorHTMLAttributes<HTMLAnchorElement>
> = (props) => <a className={styles.replyLink} {...props} />;

export const ReplySentText: FunctionComponent<{ centered?: boolean }> = ({
  centered,
  ...props
}) => (
  <div
    className={cx(styles.replySentText, centered && styles.centered)}
    {...props}
  />
);

type ReplyTextareaProps = TextareaAutosizeProps & {
  value?: string;
};

export const ReplyTextarea = (props: ReplyTextareaProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (inputRef.current) {
      const endIndex = inputRef.current.value.length;
      inputRef.current.focus();
      // There are cross-browser inconsistencies about where the cursor is
      // after calling focus(). Always select the end of the input.
      inputRef.current.setSelectionRange(endIndex, endIndex);
    }
  }, []);

  return (
    <Textarea className={styles.replyTextArea} ref={inputRef} {...props} />
  );
};
